import {
  APPOINTMENT_DURATIONS,
  appointmentDurationLabel,
  appointmentSlot,
  dayOf,
  parisInstant,
} from '@ghp/domain'
import type { WheelItem } from '@ghp/ui'

/**
 * Les roues de « Planifier le rendez-vous » (frame Terrain 2.4) : jour, mois, année, puis l'heure
 * — toutes **à l'heure de Paris**, comme la confirmation que l'usager recevra.
 */
export interface WheelSlot {
  /** `01` à `31` */
  day: string
  /** `01` à `12` */
  month: string
  year: string
  /** `HH:MM` */
  time: string
}

/** L'heure de la frame quand aucun rendez-vous n'est posé. */
const DEFAULT_TIME = '09:00'

const WEEKDAYS = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam']

export const MONTH_ITEMS: readonly WheelItem[] = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
].map((label, index) => ({ value: String(index + 1).padStart(2, '0'), label }))

/** Le créneau en place, ou aujourd'hui à 09:00 sur un contrôle sans rendez-vous. */
export function slotOf(scheduledAt: string | null, now = new Date()): WheelSlot {
  const [year = '', month = '', day = ''] = dayOf(
    scheduledAt === null ? now : new Date(scheduledAt),
  ).split('-')
  const time = scheduledAt === null ? DEFAULT_TIME : appointmentSlot(scheduledAt)?.time

  return { year, month, day, time: time ?? DEFAULT_TIME }
}

/** Les jours du mois, « lun. 28 » comme la frame — la date n'est qu'un numéro de calendrier. */
export function dayItems(year: string, month: string): WheelItem[] {
  return Array.from({ length: daysIn(year, month) }, (_, index) => {
    const value = String(index + 1).padStart(2, '0')
    const weekday =
      WEEKDAYS[new Date(Date.UTC(Number(year), Number(month) - 1, index + 1)).getUTCDay()]

    return { value, label: `${weekday ?? ''}. ${value}` }
  })
}

/** L'année en cours et les deux suivantes — plus celle du créneau en place, s'il est plus ancien. */
export function yearItems(placed: string, now = new Date()): WheelItem[] {
  const current = Number(dayOf(now).slice(0, 4))
  const years = [current, current + 1, current + 2].map(String)

  return (years.includes(placed) ? years : [placed, ...years]).map((value) => ({
    value,
    label: value,
  }))
}

/**
 * De 07:00 à 17:00 par demi-heure, bornes incluses (la boucle du prototype) — plus l'heure déjà
 * posée hors de la grille : sans elle, la roue montrerait 07:00 pour un rendez-vous à 09:15.
 */
export function timeItems(placed: string): WheelItem[] {
  const items: WheelItem[] = []
  for (let minutes = 7 * 60; minutes <= 17 * 60; minutes += 30) {
    const value = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
    items.push({ value, label: value })
  }

  return items.some((item) => item.value === placed)
    ? items
    : [...items, { value: placed, label: placed }].sort((a, b) => a.value.localeCompare(b.value))
}

/**
 * Les crans de la roue « Durée » de « Modifier le rendez-vous » (frame 2.1b, #578) — ceux de la roue
 * du bureau, de 30 min à 8 h puis 2 et 3 jours —, plus la durée déjà posée hors de ces crans, pour
 * la même raison que `timeItems`.
 */
export function durationItems(placed: string): WheelItem[] {
  const minutes: number[] = [...APPOINTMENT_DURATIONS]
  if (!minutes.includes(Number(placed))) minutes.push(Number(placed))

  return minutes
    .sort((a, b) => a - b)
    .map((value) => ({ value: String(value), label: appointmentDurationLabel(value) }))
}

/** Le jour ramené au dernier du mois quand le mois choisi est plus court (31 → février). */
export function clampedDay(slot: WheelSlot): string {
  return String(Math.min(Number(slot.day), daysIn(slot.year, slot.month))).padStart(2, '0')
}

/** L'instant à poser, à l'heure de Paris. */
export function instantOf(slot: WheelSlot): string | null {
  return parisInstant(`${slot.year}-${slot.month}-${clampedDay(slot)}`, slot.time)
}

function daysIn(year: string, month: string): number {
  return new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate()
}
